import type { VariablesOf } from "gql.tada";
import { useMemo } from "react";
// biome-ignore lint/style/useImportType: Value required for typeof in ExportFeedArticlesInput type
import { ExportFeedArticlesInCSVFormat } from "@/data-access/news-feeds/queries/news-feeds-queries";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";

export type ExportFeedArticlesInput = VariablesOf<
  typeof ExportFeedArticlesInCSVFormat
>["input"];

/**
 * Builds the mutation input for ExportFeedArticlesInCSVFormat from current
 * params and feed-content state. Returns null when feedId or date range is missing.
 */
export const useExportFeedArticlesInput = (): {
  input: ExportFeedArticlesInput | null;
} => {
  const { feedId, startDate, endDate, selectedFilters, chartSelection } =
    useAnalysisParamsForQueries();
  const ordering = useFeedContentManagementStore((state) => state.ordering);
  const searchTerm = useFeedContentManagementStore((state) => state.searchTerm);

  return useMemo(() => {
    if (feedId == null || !startDate || !endDate) {
      return { input: null };
    }
    const filters = {
      feedId,
      dateRange: { startDate, endDate },
      state: "ADDED" as const,
      searchMatch: searchTerm || undefined,
      ...(chartSelection && { chartSelection }),
      ...selectedFilters,
    };
    return {
      input: {
        filters,
        ordering: ordering.length > 0 ? ordering : null,
      },
    };
  }, [
    feedId,
    startDate,
    endDate,
    selectedFilters,
    chartSelection,
    searchTerm,
    ordering,
  ]);
};
